import { Component, Input, ViewChild, ViewEncapsulation, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { Endpoint, HelpIndex } from '../../../models/enums';
import { RestService } from '../../../services/rest/rest.service';
import { CommonService } from '../../../services/common/common.service';
import { PasswordAndUserLogoutPolicy } from '../../../models/password-user-logout-policy';
import { getErrorMessageFromHtmlResponse } from '../../../models/utils';

@Component({
    standalone: false,
    selector: 'res-change-password-modal',
    templateUrl: './change-password-modal.component.html',
    styleUrls: ['./change-password-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class ChangePasswordModalComponent extends BaseModalComponent implements OnInit {
    @Input() username: string;
    @ViewChild('changePasswordForm', { static: true }) changePasswordForm: NgForm;
    @ViewChild('newOldPwdMatchPop') newOldPwdMatchPop: PopoverDirective;
    @ViewChild('invalidCredentialsPop') invalidCredentialsPop: PopoverDirective;
    @ViewChild('passwordErrorPop') passwordErrorPop: PopoverDirective;

    modalClass = 'change-password-modal';
    modalAutomationId = 'change-password';
    modalHelpIndex = HelpIndex.myedisc_change_password_dlg;
    primaryButtonLabel = 'Save Changes';

    newPassword: string = '';
    passwordPolicyError = '';
    entityType: string;
    isEditUser = false;
    isBusy = false;
    isChangePwdNoCurrent = false;
    showInvalid = false;
    waitingForTarget = false;
    passwordValidated = false;
    readonly validationAttempts = 4;
    passwordAttempts = [];

    passwordPolicy = new PasswordAndUserLogoutPolicy();

    isSystemUserContext = false;

    constructor(
        private modalService: BsModalService,
        private changePasswordModalRef: BsModalRef,
        private restService: RestService,
        private commonService: CommonService) {
        super(modalService, changePasswordModalRef);
    }

    saveChanges(): void {
        if (this.isChangePwdNoCurrent) {
            const userNameReset = this.modalData.userName,
                newPasswordReset = this.changePasswordForm.value.newPassword;

            this.isBusy = true;
            this.restService.post(Endpoint.userManager_resetPassword, {
                newPassword: newPasswordReset,
                userName: userNameReset,
                orgName: this.commonService.currentUser?.originalCustomerName === 'super_system_customer' && this.isSystemUserContext ? 'super_system_customer' : (this.commonService?.currentUser?.customerName || this.modalData?.orgHandle),
                systemScope: this.isSystemUserContext

            }).then(response => {
                if (response.status === 'SUCCESS') {
                    this.isBusy = false;
                    this.closeModal(true);
                }
            }, error => {
                this.isBusy = false;
            });

        } else {
            let startIndex = 0,
                endIndex = 0;
            const userName = this.changePasswordForm.value.userName,
                currentPassword = this.changePasswordForm.value.currentPassword,
                newPassword = this.changePasswordForm.value.newPassword;

            if (this.invalidValue(userName) || this.invalidValue(currentPassword) ||
                this.invalidValue(newPassword)) {
                return;
            }
            this.closeAllOpenErrorPopups();
            if (newPassword !== currentPassword) {
                this.restService.getPasswordPolicy(userName, currentPassword, true, true).then(response => {
                    this.commonService.sessionToken = userName + ':' + currentPassword;
                    this.restService.post(Endpoint.userManager_changeUserPassword,
                        {
                            'oldPassword': currentPassword,
                            'newPassword': newPassword
                        }, false)
                        .then(res => {
                            this.closeAllOpenErrorPopups();
                            this.closeModal(true, newPassword);
                        }).catch(res => {
                            if (res) {
                                let errorMessage = res.error;
                                if (res.data?.Contains('unauthorized')) {
                                    this.invalidCredentialsPop.show();
                                } else if (errorMessage?.indexOf('Error report') !== -1) {
                                    startIndex = errorMessage.indexOf('must:');
                                    endIndex = errorMessage.indexOf('null') - 2;
                                    if (startIndex !== -1 && endIndex > 0) {
                                        this.passwordPolicyError = '"New Password" ' + errorMessage.slice(startIndex, endIndex);
                                    } else {
                                        const apiErrorText = getErrorMessageFromHtmlResponse(errorMessage);
                                        if (apiErrorText) {
                                            this.passwordPolicyError = apiErrorText;
                                            this.passwordErrorPop.show();
                                        }
                                    }
                                }
                            }
                        });
                }).catch(response => {
                    if (response.error && response.error.includes('The allowed number')) {
                        this.closeModal(false);
                        setTimeout(() => {
                            this.commonService.openErrorModal('The allowed number of login attempts has been exceeded. Your account has been \ntemporarily locked. Please contact your system administrator to unlock your account.');
                        });
                    } else {
                        if (response.status === 500) {
                            console.log('Password policy call failed and returned a 500' +
                                ' error status code implementing an invalid user id and password');
                        }
                        this.invalidCredentialsPop.show();
                    }
                });
            } else {
                this.newOldPwdMatchPop.show();
            }
        }

    }

    ngOnInit(): void {
        this.username = this.modalData.loginUserName;
        this.isChangePwdNoCurrent = this.modalData.entityType === 'change-password-no-current';
        this.isSystemUserContext = this.modalData?.isSystemUser;
        if (this.isChangePwdNoCurrent) {
            this.primaryButtonLabel = 'OK';
            this.entityType = this.modalData.entityType;
            this.username = this.modalData.userName;
            this.isEditUser = true;
            this.restService.getPasswordPolicy(this.username, '', !this.isChangePwdNoCurrent, true).then((response) => {
                if (response.enforceStrongPasswords) {
                    this.passwordPolicy = new PasswordAndUserLogoutPolicy(response);
                }
            });
        }
        this.storePasswordAttempt();
    }

    onPasswordChanged(): void {
        this.newPassword = this.changePasswordForm.value.newPassword;
    }

    saveEnabled(): boolean {
        const attempt = {
            username: this.changePasswordForm.value.userName,
            password: this.changePasswordForm.value.currentPassword
        };
        let found = false;
        this.passwordAttempts.some((oldAttempt) => {
            if (oldAttempt.username === attempt.username && oldAttempt.password === attempt.password) {
                found = true;
                return true;
            }
        });
        const newAttempt = this.passwordAttempts.length === this.validationAttempts && !found;
        return (newAttempt || this.changePasswordForm.valid) && (this.passwordPolicy?.enforceStrongPasswords ? this.passwordPolicy?.isPasswordPolicyRequirementsForStandardAuthPassed() : true);
    }

    focusOnPassword() {
        setTimeout(() => {
            const currentPasswordInput = document.getElementsByTagName('input').namedItem('currentPassword');
            currentPasswordInput.focus();
            currentPasswordInput.setSelectionRange(currentPasswordInput.value.length, currentPasswordInput.value.length);
        });
    }

    preventPopup() { // Called when username, current password, or the current password visibility switch icon get focus
        this.waitingForTarget = false;
    }

    checkCurrentPassword() {
        if (!this.passwordValidated && this.validationAttempts) {
            this.waitingForTarget = true;
            clearTimeout(undefined);
            setTimeout(() => {
                if (this.waitingForTarget) {  // Only test the password if the user focuses outside of the password controls
                    this.managePasswordErrorPopup();
                }
            }, 250); // The delay gives the incoming focus target a chance to clear the flag
        }
    }

    storePasswordAttempt(): boolean {
        if (this.passwordAttempts.length === this.validationAttempts) {
            return false;
        }
        const username = this.changePasswordForm.value.userName;
        const currentPassword = this.changePasswordForm.value.currentPassword;
        if (!username || !currentPassword || !username.length || !currentPassword.length) {
            return false;
        }

        const attempt = {
            username: this.changePasswordForm.value.userName,
            password: this.changePasswordForm.value.currentPassword
        };
        let found = false;
        this.passwordAttempts.some((oldAttempt) => {
            if (oldAttempt.username === attempt.username && oldAttempt.password === attempt.password) {
                found = true;
                return true;
            }
        });
        if (!found) {
            this.passwordAttempts.push(attempt);
        }
        return !found;

    }

    managePasswordErrorPopup() {
        if (this.storePasswordAttempt()) {
            const username = this.changePasswordForm.value.userName;
            const currentPassword = this.changePasswordForm.value.currentPassword;
            this.restService.getPasswordPolicy(username, currentPassword, !this.isChangePwdNoCurrent, true).then((response) => {
                if (response.enforceStrongPasswords) {
                    this.passwordPolicy = new PasswordAndUserLogoutPolicy(response);
                }
                this.passwordValidated = true;
                this.invalidCredentialsPop.hide();
            }).catch(() => {
                this.invalidCredentialsPop.show();
            });
        }
    }

    closeAllOpenErrorPopups() {
        this.newOldPwdMatchPop.hide();
        this.invalidCredentialsPop.hide();
        this.passwordErrorPop.hide();
    }

    invalidValue(val): boolean {
        return val === null || val === undefined;
    }
}
